import { NgTemplateOutlet } from '@angular/common';
import { Component, ContentChild, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { SafariInjector } from '@safarilaw-webapp/shared/common-objects-models';
import { ReduxWrapperService } from '@safarilaw-webapp/shared/redux';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { Subscription, filter, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CssLoaderService } from '../../../../scss-loader/services/style-loader.service';
import { SafariUiAccordionReduxObject } from '../../../../state/actions/layout-actions';

export enum SafariIcon {
  X = 's_ico-remove_fill'
}
@Component({
  selector: 'sl-ui-kit-safari-accordion',
  templateUrl: './safari-accordion.component.html',
  styleUrls: ['./safari-accordion.component.scss'],
  imports: [CollapseModule, NgTemplateOutlet]
})
export class SafariAccordionComponent extends SafariInjector implements OnInit, OnDestroy {
  /**
   * accordionCustomHeaderTemplate replaces the entire header. If you use this you'll be responsible for calling parent.toggle()
   * from the button that represents toggle in your design. As well as passing IDs, etc.
   *
   */
  @ContentChild('accordionCustomHeaderTemplate', { descendants: false }) accordionCustomHeaderTemplate: TemplateRef<any>;
  /**
   * accordionDefaultHeaderAdditionalHtml allows for adding some custom HTML to a specific position in the standard header (rigth of
   * the standard toggle button)
   */
  @ContentChild('accordionDefaultHeaderAdditionalHtml', { descendants: false }) accordionDefaultHeaderAdditionalHtml: TemplateRef<any>;

  @Input()
  keepStateOnDestroy = false;
  @Input()
  defaultExpanded = true;

  @HostBinding('attr.id')
  @Input()
  id: string;
  @Input()
  name: string;
  @Input()
  button1: boolean;

  /**
   * Note - while in theory SafariIcon is nothing more than a string with a CSS classname we don't want to expose that.
   * It is true that we can customize icons to any of our safari icons since they are global classes
   * but we don't want to give an impression that any random class can be passed into this.
   * For this reason label is either a free form string ('Reset to Defaults' on matter config for example)
   * OR speifically a string that 100% matches one of our icon names
   *
   * Anything more complicated and you'll need to either
   *  -- override the whole header using customheader outlet. In this case you will also need to provide a toggle
   * button and make sure to call accordions toggle when it's clicked.
   * For example
   *    <sl-ui-kit-safari-accordion #parent ...>
   *      <ng-template #accordionCustomHeaderTemplate>
   *      <button class="myToggleButton" (click)="parent.toggle()">TOGGLE</button>
   *      ... OTHER CUSTOM HEADER HTML
   *      </ng-template>
   *     <sl-ui-kit-safari-accordion>
   * OR
   *  -- disable button1 and use accordionDefaultHeaderAdditionalHtml outlet of the regular accordion header to render whatever you want
   *     In this case you are keeping general look and feel of the regular header and the default toggle button
   */
  private _button1Label: string | SafariIcon;

  isLabelAnIcon = true;
  public get button1Label(): string | SafariIcon {
    return this._button1Label;
  }
  @Input()
  public set button1Label(value: string | SafariIcon) {
    // Because of the weird way TS compiles enums we can't check for typeof enum
    // But we can check if SafariIcon includes this value and if it does it is assumed that this "label"
    // is a standard SafariIcon
    // That also means we can't have actual labels that say things like "s_ico-remove_fill" or "s_ico-garbage_fill",
    // but if that were ever to be an actual text we want to show in the header, then we probably have bigger problems.
    this.isLabelAnIcon = Object.values(SafariIcon).includes(value as any);
    this._button1Label = value;
  }

  private _headerClasses = 's_sec';

  protected cssLoaderService: CssLoaderService;

  @Input()
  set headerClasses(value: string) {
    this._headerClasses = 's_sec';
    if (value) {
      this._headerClasses = [this._headerClasses, value].join(' ');
    }
  }

  get headerClasses(): string {
    return this._headerClasses;
  }

  @Input()
  wrapperClasses = '';

  // We could have just inherited from safaribasecomponent and use super.subscribe
  // but it seems unneccesary to import all that additional stuff just for this
  private _accordionSubscriptions: Subscription[] = [];
  @Output()
  button1ClickEvent = new EventEmitter<any>();

  get isExpanded() {
    return !this.isCollapsed;
  }
  isCollapsed = true;
  get wrapperId() {
    return this.id + '-wrapper';
  }
  get headerId() {
    return this.id + '-header';
  }
  get contentId() {
    return this.id + '-content';
  }
  private _reduxWrapperService: ReduxWrapperService;
  private _accordionReduxObject: SafariUiAccordionReduxObject;
  constructor() {
    super();
    this.cssLoaderService = this.inject(CssLoaderService);
    this._reduxWrapperService = this.inject(ReduxWrapperService);
    this._accordionReduxObject = this.inject(SafariUiAccordionReduxObject);
  }

  ngOnDestroy(): void {
    for (const sub of this._accordionSubscriptions) {
      if (sub != null) {
        sub.unsubscribe();
      }
    }
    this._accordionSubscriptions = [];

    if (!this.keepStateOnDestroy) {
      this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.clearState({ payload: { id: this.id } }));
    }
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method -- in dev
  ngOnInit(): void {
    if (this.button1Label == undefined) {
      this.button1Label = SafariIcon.X;
    }
    this.cssLoaderService.load('safari-accordion');
    let state = null;
    if (this.id == null) {
      this.id = uuidv4();
    }
    // Before anything let's see if there's any state here (before dispatch activate because that would alwasy creat
    // a new state)
    this._reduxWrapperService
      .getGenericSelector(this._accordionReduxObject.default.selectors.getState(this.id))
      .pipe(take(1))
      .subscribe(o => {
        state = o;
      });
    // Always dispatch active onInit - regardless of whether there was state here before or not
    this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.setActive({ payload: { id: this.id, active: true } }));
    // If there was nothing in the state initially then set it according to this accordion's input props
    // (Presence of state in OnInit would indicate either refresh from Save OR accordion controller's settings)
    if (state == null) {
      const expanded = this.defaultExpanded;
      if (expanded) {
        this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionExpanded({ payload: { id: this.id } }));
      } else {
        this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionCollapsed({ payload: { id: this.id } }));
      }
    }

    this._accordionSubscriptions.push(
      this._reduxWrapperService.getGenericSelector(this._accordionReduxObject.default.selectors.getIsExpanded(this.id)).subscribe(isExpanded => {
        this.isCollapsed = !isExpanded;
      })
    );

    this._accordionSubscriptions.push(
      this._reduxWrapperService
        .listenForAction(this._accordionReduxObject.default.actions.accordionCollapse)
        .pipe(filter(o => o.payload.id == this.id))
        .subscribe(() => {
          this.collapseAccordion();
        })
    );
    this._accordionSubscriptions.push(
      this._reduxWrapperService
        .listenForAction(this._accordionReduxObject.default.actions.accordionExpand)
        .pipe(filter(o => o.payload.id == this.id))
        .subscribe(() => {
          this.expandAccordion();
        })
    );
  }
  private _toggleAccordion() {
    this.isCollapsed = !this.isCollapsed;
    this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionToggled({ payload: { id: this.id } }));
    if (this.isExpanded) {
      this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionExpanded({ payload: { id: this.id } }));
    } else {
      this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionCollapsed({ payload: { id: this.id } }));
    }
  }

  toggle() {
    this._toggleAccordion();
  }
  expandAccordion() {
    this.isCollapsed = false;
    this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionExpanded({ payload: { id: this.id } }));
  }
  collapseAccordion() {
    this.isCollapsed = true;
    this._reduxWrapperService.dispatchGenericAction(this._accordionReduxObject.default.actions.accordionCollapsed({ payload: { id: this.id } }));
  }

  onButton1Click() {
    this.button1ClickEvent.emit();
  }
}
